import { dayjs, daysBetween, formatDate, DATE } from '@koob/dates';
import { getSetting, getSettingLabel } from '@koob/utils';
import { koobMarker } from '@koob/map-marker';

import ExportStaticMap from './ExportStaticMap';
import ExportFlowPage from './ExportFlowPage';
import ExportInaccessibleBadge from './ExportInaccessibleBadge';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const MAP_WIDTH_PX = 284;
const MAP_HEIGHT_PX = 355;

function groupDaysByProgram(tripDays, experiences) {
  const groups = [];

  tripDays?.forEach((day, index) => {
    const experience = experiences?.find((e) => e.id === day.experienceId);
    const program = experience?.programs?.[day.experienceDayIndex];
    const last = groups[groups.length - 1];

    if (last && last.experience?.id === experience?.id) {
      last.days.push({ day, index, program });
    } else {
      groups.push({ experience, days: [{ day, index, program }] });
    }
  });

  return groups;
}

export default function ExportSummary({
  trip,
  experiences,
  tourOperator,
  settings,
  theme,
}) {
  const sectionLabel = getSettingLabel(settings, 'itinerary-summary');
  const itinerarySection = getSetting(settings, 'itinerary-summary');
  const titleLabel = getSettingLabel(
    itinerarySection?.sections,
    'itinerary-summary-title',
  );

  const daysSection = getSetting(settings, 'days');
  const dayLabel = getSettingLabel(
    getSetting(daysSection?.sections, 'program-of-the-day')?.fields,
    'pod-day-number',
  );

  const coverPageSection = getSetting(settings, 'cover-page');
  const daysLabel = getSettingLabel(
    coverPageSection?.sections,
    'cover-page-days',
  );
  const nightsLabel = getSettingLabel(
    coverPageSection?.sections,
    'cover-page-nights',
  );

  const nightsCount = daysBetween(trip?.startDate, trip?.endDate);

  const groups = groupDaysByProgram(trip?.tripDays, experiences);

  const pins = [];
  const placedPositions = new Set();
  trip?.tripDays?.forEach((day, index) => {
    const experience = experiences?.find((e) => e.id === day.experienceId);
    const position =
      experience?.programs?.[day.experienceDayIndex]?.startPosition;
    if (!position?.lat || !position?.lon) return;

    const key = `${position.lat},${position.lon}`;
    if (placedPositions.has(key)) return;
    placedPositions.add(key);

    pins.push({
      ...position,
      icon: koobMarker('day', {
        label: index + 1,
        filled: true,
        color: theme.primaryColor,
      }),
    });
  });

  const hasMap = pins?.length > 0;

  return (
    <ExportFlowPage
      sectionLabel={sectionLabel}
      title={titleLabel}
      badge={
        trip?.tripDays?.length > 0
          ? `${nightsCount + 1} ${daysLabel} · ${nightsCount} ${nightsLabel}`
          : null
      }
      trip={trip}
      tourOperator={tourOperator}
      settings={settings}
      footerStatus
      aside={
        hasMap ? (
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-5">
              <ExportStaticMap
                points={pins}
                apiKey={GOOGLE_MAPS_API_KEY}
                width={MAP_WIDTH_PX}
                height={MAP_HEIGHT_PX}
                className="w-full rounded-lg"
                style={{ height: `${MAP_HEIGHT_PX}px` }}
              />
            </div>
          </div>
        ) : null
      }
    >
      <div className="grid grid-cols-12 gap-8">
        <ol
          className={`relative space-y-6 pl-8 ${
            hasMap ? 'col-span-7 col-start-6' : 'col-span-12'
          }`}
        >
          <span
            aria-hidden
            className="absolute left-[7px] top-2 bottom-2 w-px bg-k-gray"
          />

          {groups.map((group, groupIndex) => {
            const firstDayNumber = group.days[0].index + 1;
            const lastDayNumber = group.days[group.days.length - 1].index + 1;
            const rangeLabel =
              firstDayNumber === lastDayNumber
                ? `${dayLabel} ${firstDayNumber}`
                : `${daysLabel} ${firstDayNumber}–${lastDayNumber}`;

            return (
              <li key={groupIndex} className="relative" data-prevent-cut>
                <span
                  aria-hidden
                  className="absolute -left-[26px] top-3 h-4 w-4 rounded-full border-2 border-[var(--td-paper)] bg-[var(--td-primary)]"
                />

                <div className="rounded-md border border-k-gray/40">
                  <div className="flex items-baseline justify-between gap-4 border-b border-k-gray/30 px-5 py-3">
                    <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                      <div
                        className="text-xl leading-tight"
                        style={{ fontFamily: 'var(--td-font-display)' }}
                      >
                        {group.experience?.name}
                      </div>
                      {group.experience?.isAccessible === false && (
                        <ExportInaccessibleBadge />
                      )}
                    </div>
                    <div className="flex-shrink-0 text-2xs font-semibold uppercase tracking-widest text-[var(--td-ink-muted)]">
                      {rangeLabel}
                    </div>
                  </div>

                  <ul className="divide-y divide-k-gray/30">
                    {group.days.map(({ index, program }) => (
                      <li
                        key={index}
                        className="flex items-baseline gap-4 px-5 py-2.5"
                      >
                        <span
                          className="w-8 flex-shrink-0 text-lg italic text-[var(--td-primary)]"
                          style={{ fontFamily: 'var(--td-font-display)' }}
                        >
                          {String(index + 1).padStart(2, '0')}
                        </span>
                        <span className="w-20 flex-shrink-0 text-2xs font-semibold uppercase tracking-widest text-[var(--td-ink-muted)]">
                          {formatDate(
                            dayjs(trip?.startDate).add(index, 'days'),
                            DATE,
                          )}
                        </span>
                        <span
                          className="flex-1 text-base leading-tight"
                          style={{ fontFamily: 'var(--td-font-display)' }}
                        >
                          {program?.title ?? group.experience?.name}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </ExportFlowPage>
  );
}
